export interface SiteFunctionEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fn: (args: any) => any | Promise<any>;
  description: string;
  schema?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
}

export interface ManifestEntry {
  name: string;
  description: string;
  schema?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
}

export interface BuiltinFlags { scene: boolean; theme: boolean; }

export interface Registry {
  registerSiteFunction(
    name: string,
    entry: SiteFunctionEntry,
    builtins?: BuiltinFlags,
  ): void;
  /**
   * Internal: register a site-function bypassing the reserved-name guard.
   * Used by built-ins so they can register their own reserved names
   * (e.g. 'scene', 'setTheme') without conflict.
   */
  registerSystem(name: string, entry: SiteFunctionEntry): void;
  registerSceneHandler(fn: (dsl: string) => void): void;
  registerThemeHandler(fn: (theme: 'light' | 'dark' | 'system') => void): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invokeSiteFunction(name: string, args: any): Promise<any>;
  getSceneHandler(): ((dsl: string) => void) | undefined;
  getThemeHandler(): ((theme: 'light' | 'dark' | 'system') => void) | undefined;
  serializeManifest(builtins: BuiltinFlags): ManifestEntry[];
}

export function createRegistry(): Registry {
  const fns = new Map<string, SiteFunctionEntry>();
  let sceneHandler: ((dsl: string) => void) | undefined;
  let themeHandler: ((theme: 'light' | 'dark' | 'system') => void) | undefined;

  return {
    registerSiteFunction(name, entry, builtins) {
      if (builtins?.scene && name === 'scene') {
        throw new Error(`'scene' is reserved by the built-in 'scene'`);
      }
      if (builtins?.theme && name === 'setTheme') {
        throw new Error(`'setTheme' is reserved by the built-in 'theme'`);
      }
      fns.set(name, entry);
    },
    registerSystem(name, entry) { fns.set(name, entry); },
    registerSceneHandler(fn) { sceneHandler = fn; },
    registerThemeHandler(fn) { themeHandler = fn; },
    async invokeSiteFunction(name, args) {
      const entry = fns.get(name);
      if (!entry) throw new Error(`Unknown site function: ${name}`);
      const merged = { ...(entry.defaults ?? {}), ...args };
      return await entry.fn(merged);
    },
    getSceneHandler() { return sceneHandler; },
    getThemeHandler() { return themeHandler; },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serializeManifest(_builtins) {
      const entries: ManifestEntry[] = [];
      for (const [name, e] of fns) {
        entries.push({
          name,
          description: e.description,
          schema: e.schema,
          defaults: e.defaults,
        });
      }
      return entries;
    },
  };
}
